import { useEffect, useMemo, useRef, useState } from "react";
import { CAPABILITY_ORDER, themeOf } from "@/pages/designPreview/previewTheme";

export function SceneIndex({ n }) {
  return (
    <span className="dp-scene-index" aria-hidden="true">
      {String(n).padStart(2, "0")}
    </span>
  );
}

/* ---------------------------------------------------------------------- */
/* Duotone iconography — outline (currentColor) + accent fill, one visual */
/* idea per capability so each reads as its own "product", not a generic  */
/* icon-set entry.                                                        */
/* ---------------------------------------------------------------------- */
export function Icon({ kind = "website", accent = "#fff", className = "", size = 56 }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round", strokeLinejoin: "round" };
  const box = { width: size, height: size, viewBox: "0 0 56 56", className, "aria-hidden": true };

  if (kind === "webdev") {
    return (
      <svg {...box}>
        <path d="M20 16 L9 28 L20 40" {...common} />
        <path d="M36 16 L47 28 L36 40" {...common} />
        <rect x="26" y="14" width="4" height="4" fill={accent} rx="1" />
        <path d="M31 15 L25 41" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
      </svg>
    );
  }
  if (kind === "saas") {
    return (
      <svg {...box}>
        <rect x="10" y="20" width="26" height="18" rx="2" {...common} opacity="0.55" />
        <rect x="18" y="12" width="26" height="18" rx="2" {...common} />
        <circle cx="24" cy="21" r="2" fill={accent} />
      </svg>
    );
  }
  if (kind === "admin") {
    return (
      <svg {...box}>
        <rect x="10" y="10" width="36" height="28" rx="2" {...common} />
        <path d="M10 20 H46" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
        <rect x="15" y="26" width="4" height="8" fill={accent} />
        <rect x="22" y="23" width="4" height="11" fill={accent} opacity="0.7" />
        <rect x="29" y="28" width="4" height="6" fill={accent} opacity="0.45" />
      </svg>
    );
  }
  if (kind === "ai") {
    return (
      <svg {...box}>
        <path d="M28 12 L31 25 L44 28 L31 31 L28 44 L25 31 L12 28 L25 25 Z" fill={accent} opacity="0.85" />
        <circle cx="28" cy="28" r="18" {...common} opacity="0.4" />
      </svg>
    );
  }
  if (kind === "automation") {
    return (
      <svg {...box}>
        <path d="M14 22 a14 14 0 1 1 2 12" {...common} />
        <path d="M12 15 L14 22 L21 20" {...common} />
        <circle cx="34" cy="34" r="2.4" fill={accent} />
        <circle cx="22" cy="18" r="2.4" fill={accent} opacity="0.6" />
      </svg>
    );
  }
  if (kind === "search") {
    return (
      <svg {...box}>
        <circle cx="25" cy="25" r="13" {...common} />
        <circle cx="25" cy="25" r="6" stroke="currentColor" strokeWidth="1" opacity="0.4" fill="none" />
        <circle cx="25" cy="25" r="2" fill={accent} />
        <path d="M35 35 L45 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...box}>
      <rect x="9" y="12" width="38" height="30" rx="2" {...common} />
      <path d="M9 19 H47" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
      <circle cx="13.5" cy="15.5" r="1.1" fill={accent} />
      <circle cx="17.5" cy="15.5" r="1.1" fill={accent} opacity="0.6" />
      <path d="M16 27 H34 M16 33 H28" stroke="currentColor" strokeWidth="1.1" opacity="0.55" />
    </svg>
  );
}

/* Faint full-bleed background motif per capability, sits behind card copy. */
export function CardPattern({ kind = "website", accent = "#fff" }) {
  const s = { position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.16 };
  const stroke = { stroke: accent, fill: "none", strokeWidth: 1 };
  if (kind === "ai") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        {[...Array(14)].map((_, i) => (
          <circle key={i} cx={40 + (i % 7) * 38} cy={30 + Math.floor(i / 7) * 130} r={i % 3 === 0 ? 2.2 : 1.2} fill={accent} opacity={0.5} />
        ))}
      </svg>
    );
  }
  if (kind === "automation") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <path d="M180 30 H260 L245 15 M260 30 L245 45" {...stroke} />
        <path d="M180 90 H260 L245 75 M260 90 L245 105" {...stroke} />
        <path d="M180 150 H260 L245 135 M260 150 L245 165" {...stroke} />
      </svg>
    );
  }
  if (kind === "saas") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <rect x="180" y="30" width="90" height="60" rx="3" {...stroke} />
        <rect x="195" y="55" width="90" height="60" rx="3" {...stroke} />
        <rect x="165" y="100" width="90" height="60" rx="3" {...stroke} />
      </svg>
    );
  }
  if (kind === "search") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <circle cx="240" cy="100" r="30" {...stroke} />
        <circle cx="240" cy="100" r="55" {...stroke} />
        <circle cx="240" cy="100" r="80" {...stroke} />
      </svg>
    );
  }
  if (kind === "admin") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        {[0, 1, 2, 3].map((r) => (
          <path key={r} d={`M170 ${20 + r * 40} H280`} {...stroke} />
        ))}
        {[0, 1, 2].map((c) => (
          <path key={c} d={`M${190 + c * 45} 15 V165`} {...stroke} />
        ))}
      </svg>
    );
  }
  if (kind === "webdev") {
    return (
      <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <path d="M190 40 L165 100 L190 160" {...stroke} />
        <path d="M260 40 L285 100 L260 160" {...stroke} />
      </svg>
    );
  }
  return (
    <svg style={s} viewBox="0 0 300 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
      <rect x="175" y="25" width="110" height="80" rx="3" {...stroke} />
      <path d="M175 42 H285" {...stroke} />
      <circle cx="184" cy="33.5" r="1.4" fill={accent} />
    </svg>
  );
}

/* ---------------------------------------------------------------------- */
/* Orbit diagram — DSITESOLUTION as a hub with capabilities orbiting it,  */
/* connected by animated bezier paths. Click/hover to inspect a node.     */
/* ---------------------------------------------------------------------- */
const ORBIT_R = 205;
const ORBIT_CENTER = 260;

function orbitPos(index, total) {
  const angle = -90 + index * (360 / total);
  const rad = (angle * Math.PI) / 180;
  return {
    x: ORBIT_CENTER + ORBIT_R * Math.cos(rad),
    y: ORBIT_CENTER + ORBIT_R * Math.sin(rad),
  };
}

export function OrbitDiagram({ layers = [], active, onSelect }) {
  const ordered = CAPABILITY_ORDER.map((id) => layers.find((row) => row.id === id)).filter(Boolean);
  const list = ordered.length ? ordered : layers;
  const total = list.length || 1;
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  return (
    <svg className="dp-orbit" viewBox="0 0 520 520" role="img" aria-label="DSITESOLUTION capability system">
      <circle className="dp-orbit__ring" cx={ORBIT_CENTER} cy={ORBIT_CENTER} r={ORBIT_R} />
      {list.map((row, i) => {
        const pos = orbitPos(i, total);
        const theme = themeOf(row.id);
        const isActive = active === row.id;
        const midX = (ORBIT_CENTER + pos.x) / 2 + (pos.y - ORBIT_CENTER) * 0.12;
        const midY = (ORBIT_CENTER + pos.y) / 2 - (pos.x - ORBIT_CENTER) * 0.12;
        const path = `M${ORBIT_CENTER},${ORBIT_CENTER} Q${midX},${midY} ${pos.x},${pos.y}`;
        return (
          <g key={row.id} className={isActive ? "is-on" : ""}>
            <path
              d={path}
              className="dp-orbit__link"
              style={{ stroke: theme.hue, opacity: !active || isActive ? 0.85 : 0.15 }}
            />
            {isActive && !reduceMotion ? (
              <circle r="3" fill={theme.hue} className="dp-orbit__packet">
                <animateMotion dur="1.6s" repeatCount="indefinite" path={path} />
              </circle>
            ) : null}
          </g>
        );
      })}
      <g className="dp-orbit__hub">
        <circle cx={ORBIT_CENTER} cy={ORBIT_CENTER} r="46" />
        <text x={ORBIT_CENTER} y={ORBIT_CENTER - 3}>DSITE</text>
        <text x={ORBIT_CENTER} y={ORBIT_CENTER + 11}>SOLUTION</text>
      </g>
      {list.map((row, i) => {
        const pos = orbitPos(i, total);
        const theme = themeOf(row.id);
        const isActive = active === row.id;
        return (
          <g
            key={`node-${row.id}`}
            className={`dp-orbit__node${isActive ? " is-on" : ""}`}
            transform={`translate(${pos.x} ${pos.y})`}
            style={{ opacity: !active || isActive ? 1 : 0.4, "--dp-node-hue": theme.hue }}
            onMouseEnter={onSelect ? () => onSelect(row.id) : undefined}
            onFocus={onSelect ? () => onSelect(row.id) : undefined}
            onClick={onSelect ? () => onSelect(row.id) : undefined}
            tabIndex={onSelect ? 0 : -1}
            role={onSelect ? "button" : undefined}
          >
            <circle r="34" className="dp-orbit__node-ring" />
            <foreignObject x="-17" y="-17" width="34" height="34">
              <Icon kind={row.id} accent={theme.hue} size={34} />
            </foreignObject>
            <text y="52">{theme.label || row.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ---------------------------------------------------------------------- */
/* Metric scene — count-up ringed by small ticks representing the 7       */
/* capability areas, tying the number back into the same system.         */
/* ---------------------------------------------------------------------- */
export function MetricOrbit() {
  const ids = CAPABILITY_ORDER;
  return (
    <svg className="dp-metric-orbit" viewBox="0 0 640 640" aria-hidden="true">
      <circle cx="320" cy="320" r="230" className="dp-metric-orbit__ring" />
      <circle cx="320" cy="320" r="270" className="dp-metric-orbit__ring" />
      {ids.map((id, i) => {
        const pos = orbitPos(i, ids.length);
        const scale = 640 / 520;
        const cx = 320 + (pos.x - ORBIT_CENTER) * scale * 1.28;
        const cy = 320 + (pos.y - ORBIT_CENTER) * scale * 1.28;
        return <circle key={id} cx={cx} cy={cy} r="4" fill={themeOf(id).hue} className="dp-metric-orbit__dot" style={{ animationDelay: `${i * 0.4}s` }} />;
      })}
    </svg>
  );
}

/* ---------------------------------------------------------------------- */
/* Authority spine — one connected vertical thread linking every proof    */
/* point instead of a strip of interchangeable cards.                    */
/* ---------------------------------------------------------------------- */
export function AuthoritySpine({ items = [] }) {
  const railRef = useRef(null);
  const [drawn, setDrawn] = useState(0);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDrawn(100);
      return undefined;
    }
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = rect.height + vh;
      const passed = vh - rect.top;
      const pct = Math.max(0, Math.min(100, (passed / total) * 100));
      setDrawn(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="dp-spine" ref={railRef}>
      <div className="dp-spine__rail">
        <div className="dp-spine__fill" style={{ height: `${drawn}%` }} />
      </div>
      <ol className="dp-spine__list">
        {items.map((item, i) => (
          <li key={item.id || i} className="dp-spine__item" style={{ "--dp-node-hue": item.hue || "#fff" }}>
            <span className="dp-spine__dot" />
            <p className="dp-spine__n">{String(i + 1).padStart(2, "0")}</p>
            <h3>{item.title}</h3>
            {item.lead ? <strong>{item.lead}</strong> : null}
            {item.body ? <p>{item.body}</p> : null}
            {item.meta ? <p className="dp-spine__meta">{item.meta}</p> : null}
            {item.link ? (
              <a href={item.link.href} target="_blank" rel="noreferrer">{item.link.label}</a>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Guide category cluster — small interactive node graph that filters the */
/* index list on hover, giving the hub a "platform" feel over a blog list.*/
/* ---------------------------------------------------------------------- */
export function CategoryCluster({ categories = [], counts = {}, hovered, onHover, hueOf }) {
  const total = categories.length || 1;
  const positioned = useMemo(
    () =>
      categories.map((cat, i) => {
        const angle = -90 + i * (360 / total);
        const rad = (angle * Math.PI) / 180;
        const r = 78;
        return { ...cat, x: 110 + r * Math.cos(rad), y: 100 + r * Math.sin(rad) * 0.82 };
      }),
    [categories, total]
  );

  return (
    <svg className="dp-cluster" viewBox="0 0 220 200" role="img" aria-label="가이드 주제 지도">
      {positioned.map((cat) => (
        <line
          key={`l-${cat.id}`}
          x1="110"
          y1="100"
          x2={cat.x}
          y2={cat.y}
          className="dp-cluster__link"
          style={{ opacity: !hovered || hovered === cat.id ? 0.5 : 0.12 }}
        />
      ))}
      <circle cx="110" cy="100" r="20" className="dp-cluster__hub" />
      {positioned.map((cat) => {
        const n = counts[cat.id] || 0;
        const r = 9 + Math.min(9, n);
        const hue = hueOf(cat.id);
        return (
          <g
            key={cat.id}
            transform={`translate(${cat.x} ${cat.y})`}
            className="dp-cluster__node"
            style={{ opacity: !hovered || hovered === cat.id ? 1 : 0.3, cursor: "pointer" }}
            onMouseEnter={() => onHover(cat.id)}
            onMouseLeave={() => onHover(null)}
          >
            <circle r={r} style={{ fill: hue, opacity: 0.22, stroke: hue }} />
            <text y="-4" className="dp-cluster__label">{cat.label}</text>
            <text y="9" className="dp-cluster__count">{n}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setPct(total > 0 ? Math.min(100, (h.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="dp-progress" aria-hidden="true">
      <div className="dp-progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function FrameField({ children, watermark }) {
  return (
    <div className="dp-frame">
      {watermark ? <span className="dp-frame__mark">{watermark}</span> : null}
      <span className="dp-frame__c dp-frame__c--tl" />
      <span className="dp-frame__c dp-frame__c--tr" />
      <span className="dp-frame__c dp-frame__c--bl" />
      <span className="dp-frame__c dp-frame__c--br" />
      {children}
    </div>
  );
}
